import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import {Card, Grid, Button, Dropdown, Input} from 'semantic-ui-react';
import {dateFormatPicker, noTimeZoneShortISODate} from '../../../common/constants/datesFormat';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {resources, scopes} from '../../../common/permissions/dictionary';
import {paths} from '../../../common/dictionaries/urls';
import {Actions} from '../../../common/actions';
import OrdersListTable from './table';
import './list.scss';

const {
  navigateTo,
  fetchOrderAndTasksStatus,
  fetchJDEParameters,
  fetchChassisForQualityAssuranceOrders,
  fetchingQualityAssuranceOrdersList,
  setQualityAssuranceFilter,
  clearQualityAssuranceFilter,
  downloadQualityAssuranceOrdersList,
} = Actions;

const outsourcedOptions = [
  {'text': 'Todos', 'value': 'all'}, {'text': 'Si', 'value': 'S'}, {'text': 'No', 'value': 'N'}];

const OFFSET = 0;

const QualityControlOrders = () => {
  const [showTable, setShowTable] = useState(true);
  const loading = useSelector((state) => state.qualityAssuranceOrders.loadingOrders);
  const pageSize = useSelector((state) => state.qualityAssuranceOrders.pageSize);
  const loadingChassis = useSelector((state) => state.qualityAssuranceOrders.loadingChassis);
  const chassisList = useSelector((state) => state.qualityAssuranceOrders.chassisList);
  const loadingOrderStatus = useSelector((state) => state.qualityAssuranceOrders.loadingOrderStatus);
  const orderStatusList = useSelector((state) => state.accessoriesOrders.orderStatusList);
  const filters = useSelector((state) => state.qualityAssuranceOrders.filters);
  const permissions = useSelector((state) => state.session.permissions);

  const dispatch = useDispatch();

  const validateFilters = (() => {
    return Object.keys(filters).reduce((acc, x) => {
      if (acc === true) {
        return true;
      }
      return (x !== 'outsourced' && filters[x]) ? true : false;
    });
  });
  
  const handleSearchClick = (value) => {
    const filterCompleted = validateFilters();
    
    if (!filterCompleted) {
      return;
    }

    if (!showTable) {
      setShowTable(true);
    }
    dispatch(fetchingQualityAssuranceOrdersList(
      filters.outsourced === 'all' ? {...filters, 'outsourced': ''} : filters, pageSize, value));
  };

  useEffect(() => {
    if (!hasAccessPermission(permissions, resources.QualityAssuranceOrders, scopes.Create)) {
      dispatch(navigateTo(paths.AccessDenied));
    }
    dispatch(fetchOrderAndTasksStatus({'type': 'order'}));
    dispatch(fetchJDEParameters());
    dispatch(fetchChassisForQualityAssuranceOrders());
  }, []);

  const clearInfo = () => {
    if (showTable) {
      setShowTable(false);
    }
  };

  const changeStartDate = (date) => {
    const dateFormatted = date ? moment(date).format(noTimeZoneShortISODate) : '';
    if (dateFormatted === filters.startDate) {
      return;
    }
    dispatch(setQualityAssuranceFilter({...filters, 'startDate': dateFormatted}));
    clearInfo();
  };

  const changeEndDate = (date) => {
    const dateFormatted = date ? moment(date).format(noTimeZoneShortISODate) : '';
    if (dateFormatted === filters.endDate) {
      return;
    }
    dispatch(setQualityAssuranceFilter({...filters, 'endDate': dateFormatted}));
    clearInfo();
  };

  const handleClearClick = () => {
    dispatch(clearQualityAssuranceFilter());
    clearInfo();
  };

  const handleCreateClick = () => {
    dispatch(navigateTo(paths.QualityControlAccessoriesOrdersCreate));
  };

  const getOptions = (list, valueField, textField) => {
    return list.map((item) => {
      return {'value': item[valueField], 'text': item[textField]};
    });
  };

  const onChangeFilterValue = (value, field) => {
    dispatch(setQualityAssuranceFilter({...filters, [field]: value}));
  };

  const exportQualityAssuranceOrdersList = (e) => {
    e.preventDefault();
    const exportFilter = {...filters, 'outsourced': filters.outsourced === 'all' ? '' : filters.outsourced};
    dispatch(downloadQualityAssuranceOrdersList(exportFilter));
  };

  const renderTitle = (width, label) => {
    return (
      <Grid.Column width={width}>
        <h4>{label}</h4>
      </Grid.Column>
    );
  };

  return (
    <div className='quality-assurance-orders-list'>
      <Card raised fluid>
        <div className='title-bar'>
          <div className='title'>
            <h2>Órdenes de Control de Calidad</h2>
            <div className='title-bar-actions'>
              {<Button secondary size='small' className='add-btns-pading' compact
                disabled={loading}
                loading={loading}
                onClick={(e) => exportQualityAssuranceOrdersList(e)}>
                <img src={`${process.env.PUBLIC_URL}/icons/excel.svg`} alt='excel icon' width={25} />
              </Button>}
              <Button secondary disabled={loading}
                onClick={handleClearClick}>Limpiar</Button>
              <Button primary disabled={loading || !validateFilters()}
                onClick={() => handleSearchClick(OFFSET)}>Buscar</Button>
              <Button primary disabled={loading} onClick={handleCreateClick}>
                Crear
              </Button>
            </div>
          </div>
          <div className='filters-section'>
            <Grid textAlign='left'>
              <Grid.Row>
                {renderTitle(2, 'Fecha Inicio')}
                {renderTitle(2, 'Fecha Fin')}
                {renderTitle(4, 'Chasis')}
                {renderTitle(2, 'Orden')}
                {renderTitle(4, 'Estado')}
                {renderTitle(2, 'Subcontrato')}
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={2}>
                  <DatePicker
                    customInput={<Input icon='calendar' size='small' />}
                    dateFormat={dateFormatPicker}
                    selected={filters.startDate ?
                      moment(filters.startDate, noTimeZoneShortISODate).toDate() : null}
                    onChange={changeStartDate.bind(this)}
                    locale='es'
                    placeholderText='Fecha Inicio'
                    disabled={loading}
                  />
                </Grid.Column>
                <Grid.Column width={2}>
                  <DatePicker
                    customInput={<Input icon='calendar' size='small' />}
                    dateFormat={dateFormatPicker}
                    selected={filters.endDate ?
                      moment(filters.endDate, noTimeZoneShortISODate).toDate() : null}
                    onChange={changeEndDate.bind(this)}
                    minDate={filters.startDate && moment(filters.startDate).toDate()}
                    placeholderText='Fecha Fin'
                    disabled={loading}
                    locale='es'
                  />
                </Grid.Column>
                <Grid.Column width={4}>
                  <Dropdown
                    value={filters.chassis}
                    options={getOptions(chassisList, 'chassis', 'chassis')}
                    selection
                    search
                    labeled
                    clearable
                    loading={loadingChassis}
                    disabled={loadingChassis || loading}
                    noResultsMessage='Chasis no encontrado'
                    onChange={(e, {value}) => onChangeFilterValue(value, 'chassis')
                    }
                  />
                </Grid.Column>
                <Grid.Column width={2}>
                  <Input
                    placeholder='Orden'
                    value={filters.order}
                    disabled={loading}
                    onChange={(e, {value}) =>
                      onChangeFilterValue(value, 'order')
                    }
                  />
                </Grid.Column>
                <Grid.Column width={4}>
                  <Dropdown
                    value={filters.status}
                    options={getOptions(orderStatusList, 'id', 'description')}
                    selection
                    search
                    loading={loadingOrderStatus}
                    disabled={loadingOrderStatus || loading}
                    noResultsMessage='Estado no encontrado'
                    onChange={(e, {value}) =>
                      onChangeFilterValue(value, 'status')
                    }
                  />
                </Grid.Column>
                <Grid.Column width={2}>
                  <Dropdown
                    value={filters.outsourced}
                    options={outsourcedOptions}
                    selection
                    search
                    disabled={loading}
                    onChange={(e, {value}) =>
                      onChangeFilterValue(value, 'outsourced')
                    }
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </div>
        </div>
      </Card>

      {showTable && <OrdersListTable parameters={filters} />}
    </div>
  );
};

export default QualityControlOrders;
